import { formattedCurrency, withStyles, AccountBalanceResponse } from 'vopay';
import { FunctionComponent, useRef, RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavMenu } from '@/providers/NavMenu.provider';

import LottieFile from '../../components/LottieFile';
import LottieBalance from '../../LottieFiles/Balances.json';
import ExpandCard from '../../components/ExpandCard';

import type { LottieRefCurrentProps } from 'lottie-react';

import styles from './InteractiveInfo.module.scss';

import { Title } from 'UI';

const cn = withStyles(styles);

type TotalBalanceCardProps = {
  accountBalanceResponse: AccountBalanceResponse;
};

type FC = FunctionComponent<TotalBalanceCardProps>;

const TotalBalanceCard: FC = ({ accountBalanceResponse }) => {
  const { t } = useTranslation('translations', { keyPrefix: 'HomepageRevamp' });
  const { onSubNavClick } = useNavMenu();
  const balanceRef = useRef<LottieRefCurrentProps>(null);

  const handleLottieFileHover = (
    ref: RefObject<LottieRefCurrentProps | null>,
    isEnter: boolean,
    frameRange: [number, number] = [0, 45],
  ) => {
    if (!ref.current) return;

    ref.current.pause();
    const [start, end] = frameRange;

    if (isEnter) {
      ref.current.setDirection(1);
      ref.current.playSegments([start, end], true);
    } else {
      ref.current.setDirection(-1);
      ref.current.playSegments([end, start], true);
    }
  };

  return (
    <ExpandCard
      align="start"
      buttonText={t('Widgets.InteractiveInfo.openAccountSummary')}
      onMouseOver={() => handleLottieFileHover(balanceRef, true)}
      onMouseOut={() => handleLottieFileHover(balanceRef, false)}
      onNavigateClick={() => onSubNavClick('accountSummary', '/account-summary', t('balances'))}
      top={
        <div className={cn('totalBalanceTopContainer')}>
          <div className={cn('totalBalanceTop')}>
            <Title level="6" emphasized={false} className={cn('totalCardLabel')}>
              {t('Widgets.InteractiveInfo.totalBalance')}
            </Title>
            <div className={cn('totalCardCurrency')}>
              <Title level="5" emphasized={false}>
                {accountBalanceResponse?.Currency}
              </Title>
              <Title level="3" className={cn('totalCardAmount')}>
                {formattedCurrency(accountBalanceResponse?.AccountBalance)}
              </Title>
            </div>
          </div>
          <LottieFile animationData={LottieBalance} lottieRef={balanceRef} />
        </div>
      }
      contents={
        <div className={cn('totalBalanceContents')}>
          <div className={cn('totalBalanceCard')}>
            <div>{t('Widgets.InteractiveInfo.currentBalance')}</div>
            <div>
              {accountBalanceResponse?.Currency} {formattedCurrency(accountBalanceResponse?.AccountBalance)}
            </div>
          </div>
          <div className={cn('totalBalanceCard')}>
            <div>{t('Widgets.InteractiveInfo.pendingBalance')}</div>
            <div>
              {accountBalanceResponse?.Currency} {formattedCurrency(accountBalanceResponse?.PendingFunds)}
            </div>
          </div>
          <div className={cn('totalBalanceCard')}>
            <div>{t('Widgets.InteractiveInfo.reserveBalance')}</div>
            <div>
              {accountBalanceResponse?.Currency} {formattedCurrency(accountBalanceResponse?.Reserve)}
            </div>
          </div>
          <div className={cn('totalBalanceCard')}>
            <div>{t('Widgets.InteractiveInfo.availableFunds')}</div>
            <div>
              {accountBalanceResponse?.Currency} {formattedCurrency(accountBalanceResponse?.AvailableFunds)}
            </div>
          </div>
        </div>
      }
    />
  );
};

export default TotalBalanceCard;
